import { Typography, RadioGroup } from "@mui/material";
import { AddressFormType, AddressTypeEnum } from "../../../types";
import AddressForm from "./AddressForm";
import {
  Box,
  Button,
  FlexColBox,
  Icon,
  Radio,
  Checkbox,
} from "../../../common/components";
import { spacing, palette } from "../../../styles/theme";
import { useTranslation } from "react-i18next";
import {
  formatAddressFormTypeToString,
  formatAddressToString,
} from "../../../utils/userAddress.utils";
import CAProvinces from "../provinces.json";
import USStates from "../states.json";
import { createStyles } from "../../../styles/theme";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useUserAddress } from "../../../common/hooks/useUserAddress";
import UserAddressService from "../../../service/UserAddressService";
import { FormContext } from "../../../context";
import {
  getEmptyAddress,
  userAddressToUserAddressFormType,
} from "../../../utils/userAddress.utils";
import { useCurrency } from "../../../common/hooks/useCurrency";
import { useSelector } from "../../../common/hooks/useSelector";
import { useAuth } from "../../../common/hooks/useAuth";
import { addressSchema } from "../../Cart/CartSchema";
import { useStore } from "react-redux";
import { CART_ACTION_ENUM } from "../../../common/reducers/cart";

const styles = createStyles({
  container: {
    backgroundColor: palette.white,
    padding: { xs: spacing.wrapperMobile, lg: spacing.wrapperDesktop },
    border: `1px solid ${palette.grey.lightGrey}`,
    borderRadius: { xs: "0px", lg: "8px" },
    width: "100%",
  },
});

interface Props {
  currentAddress: AddressFormType;
  addressType: AddressTypeEnum;
}

const ResusableAddressForm = (props: Props) => {
  const { t } = useTranslation();
  const { currentAddress } = props;
  const { isDistributor, isAdmin } = useAuth();
  const formFieldKey = `${props.addressType}Address`;
  const [isSavingAddress, setIsSavingAddress] = useState(!isAdmin);
  const { dispatch } = useStore();
  const { setUSD, setCAD } = useCurrency();

  const {
    isUserAddressLoaded,
    shippingAddresses,
    billingAddresses,
    createUserAddress,
    getAllUserAddresses,
    addOneTimeUsedAddress,
    deleteOneTimeUsedAddress,
  } = useUserAddress();

  const addresses =
    props.addressType === "shipping" ? shippingAddresses : billingAddresses;

  const { formik } = useContext(FormContext);
  const { country } = useSelector((state) => state.userInfo);

  const [isFormOpened, setIsFormOpened] = useState(false);

  const [isSelectingAddress, setIsSelectingAddress] = useState(false);
  const [previousSelectedAddress, setPreviousSelectedAddress] =
    useState<AddressFormType | null>(null);

  useEffect(() => {
    if (isUserAddressLoaded) {
      if (props.addressType === "shipping") {
        setIsFormOpened(!addresses.length);
      } else {
        setIsSelectingAddress(
          !addresses.length && currentAddress.id !== "default"
        );
      }
    }
    // eslint-disable-next-line
  }, [isUserAddressLoaded, props.addressType]);

  useEffect(() => {
    if (
      props.addressType === AddressTypeEnum.BILLING &&
      formik?.values.sameAsShippingAddress &&
      !shippingAddresses.length
    ) {
      setIsSelectingAddress(true);
    }
    // eslint-disable-next-line
  }, [addresses, props.addressType, formik, shippingAddresses]);

  const hasErrors = useMemo(() => {
    return isFormOpened
      ? !addressSchema(true).isValidSync(currentAddress)
      : false;
  }, [currentAddress, isFormOpened]);

  useEffect(() => {
    if (props.addressType === AddressTypeEnum.SHIPPING) {
      dispatch({
        type: CART_ACTION_ENUM.SET_SHIPPING_FORM_STATES,
        payload:
          isFormOpened && addressSchema(true).isValidSync(currentAddress)
            ? {
                isShippingFormOpenedAndValid: true,
                hasCheckedSaveAndContinue: isSavingAddress,
              }
            : {
                isShippingFormOpenedAndValid: false,
                hasCheckedSaveAndContinue: false,
              },
      });
    } else {
      dispatch({
        type: CART_ACTION_ENUM.SET_BILLING_FORM_STATES,
        payload:
          isFormOpened && addressSchema(true).isValidSync(currentAddress)
            ? {
                isBillingFormOpenedAndValid: true,
                hasCheckedSaveAndContinue: isSavingAddress,
              }
            : {
                isBillingFormOpenedAndValid: false,
                hasCheckedSaveAndContinue: false,
              },
      });
    }
  }, [
    isFormOpened,
    currentAddress,
    props.addressType,
    isSavingAddress,
    dispatch,
  ]);

  useEffect(() => {
    if (isUserAddressLoaded && addresses.length && !currentAddress.id) {
      formik?.setFieldValue(
        formFieldKey,
        (isDistributor || isAdmin) &&
          props.addressType === AddressTypeEnum.BILLING
          ? {
              ...getEmptyAddress(country),
              id: "default",
            }
          : userAddressToUserAddressFormType(addresses[0])
      );
      if (props.addressType === AddressTypeEnum.BILLING) {
        formik?.setFieldValue("sameAsShippingAddress", false);
      }
    }
    // eslint-disable-next-line
  }, [addresses, isUserAddressLoaded]);

  const handleDeleteAddress = useCallback(
    async (addressId: string) => {
      if (addressId) {
        if (addressId === "one_time_used") {
          deleteOneTimeUsedAddress(props.addressType);
        } else {
          await UserAddressService.deleteUserAddress(addressId);
          await getAllUserAddresses();
        }

        // If the address is the current address and the user is not a distributor or admin, set the next address as the current address
        if (currentAddress.id === addressId) {
          if (
            (isDistributor || isAdmin) &&
            props.addressType === AddressTypeEnum.BILLING
          ) {
            formik?.setFieldValue(formFieldKey, {
              ...getEmptyAddress(country),
              id: "default",
            });
          } else {
            const nextAddress = addresses.find((a) => a.id !== addressId);
            //billing address
            if (
              props.addressType === AddressTypeEnum.BILLING &&
              !nextAddress?.id
            ) {
              formik?.setFieldValue("sameAsShippingAddress", true);
            } else {
              //shipping address
              formik?.setFieldValue(
                formFieldKey,
                nextAddress
                  ? userAddressToUserAddressFormType(nextAddress)
                  : getEmptyAddress(country)
              );
              setIsFormOpened(!nextAddress);
              //if same as shipping address, set billing address to next address, if not, set billing address to default
              if (formik?.values.sameAsShippingAddress) {
                formik?.setFieldValue(
                  "billingAddress",
                  nextAddress
                    ? userAddressToUserAddressFormType(nextAddress)
                    : getEmptyAddress(country)
                );
              }
            }
          }
        }
      }
    },
    [
      addresses,
      country,
      currentAddress.id,
      deleteOneTimeUsedAddress,
      formFieldKey,
      formik,
      getAllUserAddresses,
      isAdmin,
      isDistributor,
      props.addressType,
    ]
  );

  const onSubmit = useCallback(async () => {
    if (isSavingAddress) {
      if (!currentAddress.id || currentAddress.id === "one_time_used") {
        const newAddressRes = await createUserAddress({
          ...currentAddress,
          id: undefined,
          type: props.addressType,
        });
        await formik?.setFieldValue(formFieldKey, newAddressRes.data);
        await getAllUserAddresses();

        if (
          props.addressType === AddressTypeEnum.SHIPPING &&
          formik?.values.sameAsShippingAddress
        ) {
          formik?.setFieldValue("billingAddress", newAddressRes.data);
        }

        if (props.addressType === AddressTypeEnum.BILLING) {
          await formik?.setFieldValue("sameAsShippingAddress", false);
        }
      } else {
        const address = addresses.find((a) => a.id === currentAddress.id);
        if (address) {
          await formik?.setFieldValue(formFieldKey, currentAddress);
        }
        if (
          props.addressType === AddressTypeEnum.BILLING &&
          currentAddress.id !== formik?.values.shippingAddress.id &&
          formik?.values.sameAsShippingAddress
        ) {
          await formik?.setFieldValue("sameAsShippingAddress", false, true);
        }
      }
    } else {
      const oneTimeAddress = {
        ...currentAddress,
        id: "one_time_used",
      };
      await formik?.setFieldValue(formFieldKey, oneTimeAddress, true);
      addOneTimeUsedAddress(oneTimeAddress, props.addressType);
    }

    setIsSelectingAddress(false);
    setIsFormOpened(false);
    formik?.validateForm();
  }, [
    isSavingAddress,
    currentAddress,
    formik,
    createUserAddress,
    props.addressType,
    formFieldKey,
    getAllUserAddresses,
    addresses,
    addOneTimeUsedAddress,
  ]);

  const onFormClose = useCallback(() => {
    setIsSelectingAddress(true);
    setIsFormOpened(false);

    if (previousSelectedAddress || addresses[0]) {
      formik?.setFieldValue(
        formFieldKey,
        previousSelectedAddress ??
          userAddressToUserAddressFormType(addresses[0])
      );
    } else {
      if (props.addressType === AddressTypeEnum.SHIPPING) {
        formik?.setFieldValue(formFieldKey, getEmptyAddress(country));
      } else {
        formik?.setFieldValue(formFieldKey, formik?.values.shippingAddress);
        formik?.setFieldValue("sameAsShippingAddress", true);
      }
    }
  }, [
    addresses,
    country,
    formFieldKey,
    formik,
    previousSelectedAddress,
    props.addressType,
  ]);

  const onFieldChange = useCallback(
    (e: any, field: keyof AddressFormType) => {
      formik?.setFieldValue(`${formFieldKey}.${field}`, e.target.value);
    },
    [formik, formFieldKey]
  );

  const onCountryChange = useCallback(
    (e: any) => {
      const newCountry = e.target.value;

      formik?.setFieldValue(`${formFieldKey}.country`, newCountry);

      // Update the states and select the first one
      const stateList = newCountry === "US" ? USStates : CAProvinces;
      const newCountryState = stateList[0];
      formik?.setFieldValue(`${formFieldKey}.state`, newCountryState);

      if (newCountry === "US") {
        setUSD();
      } else {
        setCAD();
      }
    },
    [formFieldKey, formik, setCAD, setUSD]
  );

  return (
    <Box
      display="flex"
      flexDirection={"column"}
      gap={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
      data-test={`${formFieldKey}.container`}
      sx={styles.container}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h2">
          {t(`checkout.${props.addressType}_address`)}
        </Typography>
        {!isFormOpened && !isSelectingAddress && (
          <Button
            onClick={() => setIsSelectingAddress(true)}
            variant="text"
            data-test={`${formFieldKey}.changeButton`}
          >
            <Typography variant="textRegular" color={palette.brand.secondary}>
              {t("general.change")}
            </Typography>
          </Button>
        )}
        {isFormOpened && props.addressType === AddressTypeEnum.BILLING && (
          <Button
            onClick={() => {
              formik?.setFieldValue("billingAddress", {
                ...formik?.values.shippingAddress,
                id: "one_time_used",
                ein: undefined,
                type: AddressTypeEnum.BILLING,
              });
            }}
            variant="text"
            data-test={`${formFieldKey}.changeButton`}
          >
            <Typography variant="textRegular" color={palette.brand.secondary}>
              {t("checkout.copy_from_delivery")}
            </Typography>
          </Button>
        )}
      </Box>
      {isFormOpened ? (
        <>
          <AddressForm
            address={currentAddress}
            onAddressFieldChange={onFieldChange}
            onAddressCountryChange={onCountryChange}
            data-test={`${formFieldKey}.form`}
            autoCompletePrefix={props.addressType}
            addressType={props.addressType}
          />
          <FlexColBox gap={"8px"}>
            <Checkbox
              label={t("checkout.save_address")}
              checked={isSavingAddress}
              onChange={() => setIsSavingAddress(!isSavingAddress)}
            />
            <Box display="flex" gap={"8px"}>
              <Button
                onClick={onSubmit}
                sx={{ width: "fit-content" }}
                size="small"
                disabled={hasErrors}
                data-test={`${formFieldKey}.saveAndContinueButton`}
              >
                {isSavingAddress
                  ? t("general.saveAndContinue")
                  : t("general.continue")}
              </Button>
              <Button
                onClick={onFormClose}
                sx={{
                  width: "fit-content",
                  color: palette.brand.primary,
                  borderColor: palette.brand.primary,
                  "&:hover": {
                    borderColor: palette.brand.secondary,
                    color: palette.brand.secondary,
                    backgroundColor: palette.white,
                  },
                }}
                size="small"
                variant="outlined"
                data-test={`${formFieldKey}.cancelButton`}
              >
                {t("general.cancel")}
              </Button>
            </Box>
          </FlexColBox>
        </>
      ) : isSelectingAddress ? (
        <FlexColBox
          alignItems="flex-start"
          gap={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
        >
          <FlexColBox
            gap={{ xs: spacing.mediumMobile, lg: spacing.mediumDesktop }}
          >
            <RadioGroup
              sx={{
                gap: { xs: spacing.mediumMobile, lg: spacing.mediumDesktop },
              }}
            >
              {(isDistributor || isAdmin) &&
                props.addressType === AddressTypeEnum.BILLING && (
                  <Radio
                    label={t("checkout.use_default_billing_address")}
                    data-test={`${formFieldKey}.defaultRadio`}
                    onChange={() => {
                      formik?.setFieldValue(formFieldKey, {
                        ...getEmptyAddress(country),
                        id: "default",
                      });
                    }}
                    checked={currentAddress.id === "default"}
                  />
                )}
              {props.addressType === AddressTypeEnum.BILLING && (
                <Radio
                  label={t("checkout.same_as_shipping")}
                  data-test={`${formFieldKey}.sameAsShippingRadio`}
                  onChange={() => {
                    formik?.setFieldValue(formFieldKey, {
                      ...getEmptyAddress(country),
                      id: "default-shipping",
                    });
                  }}
                  checked={currentAddress.id === "default-shipping"}
                />
              )}
              {addresses.map((a) => (
                <Radio
                  key={a.id}
                  data-test={`${formFieldKey}.addressRadio`}
                  label={
                    <Typography
                      variant="textRegular"
                      color={palette.grey.primary}
                    >
                      {formatAddressToString(a, true)} (
                      <Typography
                        variant="textRegular"
                        color={palette.brand.secondary}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleDeleteAddress(a.id);
                        }}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {t("general.delete")}
                      </Typography>
                      )
                    </Typography>
                  }
                  onChange={() => {
                    formik?.setFieldValue(
                      formFieldKey,
                      userAddressToUserAddressFormType(a)
                    );
                  }}
                  checked={a.id === currentAddress.id}
                />
              ))}
            </RadioGroup>
            <Box
              display="flex"
              onClick={() => {
                setIsFormOpened(true);
                setPreviousSelectedAddress(currentAddress);
                formik?.setFieldValue(formFieldKey, getEmptyAddress(country));
              }}
              sx={{ cursor: "pointer" }}
              gap={"4px"}
              alignItems="center"
              data-test={`${formFieldKey}.addNewButton`}
            >
              <Icon icon="add" size={18} color={palette.brand.secondary} />
              <Typography variant="textRegular" color={palette.brand.secondary}>
                {t("checkout.add_new_address")}
              </Typography>
            </Box>
          </FlexColBox>

          {(props.addressType === AddressTypeEnum.BILLING ||
            !!addresses.length) && (
            <Button
              onClick={() => setIsSelectingAddress(false)}
              sx={{ width: "fit-content" }}
              size="small"
              disabled={!currentAddress.id}
              data-test={`${formFieldKey}.continueButton`}
            >
              {t("general.continue")}
            </Button>
          )}
        </FlexColBox>
      ) : (
        <Typography
          variant="textRegular"
          color={palette.grey.primary}
          data-test={`${formFieldKey}.formattedAddress`}
        >
          {currentAddress.id
            ? currentAddress.id === "default"
              ? t("checkout.use_default_billing_address")
              : currentAddress.id === "default-shipping"
                ? t("checkout.same_as_shipping")
                : formatAddressFormTypeToString(currentAddress, true)
            : ""}
        </Typography>
      )}
    </Box>
  );
};

export default ResusableAddressForm;
